import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Trophy, Skull, Shield, TrendingDown, TrendingUp, ExternalLink } from "lucide-react";
import { useTeams } from "@/lib/useTeams";
import { toast } from "sonner";
import api from "@/lib/api";

function formatTime(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function BossHistory() {
  const teams = useTeams();
  const navigate = useNavigate();
  const [team, setTeam] = useState("Monday");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await api.get("/boss-history", { params: { team } });
      setData(res.data);
    } catch {
      toast.error("Failed to load boss history");
    }
    setLoading(false);
  };

  useEffect(() => { loadHistory(); }, [team]);

  const bosses = data?.bosses || [];

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="boss-history-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase">Boss History</h1>
          <p className="text-muted-foreground text-sm mt-1">Kill times and progression by team</p>
        </div>
        <Select value={team} onValueChange={setTeam}>
          <SelectTrigger data-testid="history-team-select" className="w-40 h-8 text-xs bg-card/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {teams.map((t) => <SelectItem key={t} value={t}>{t} Team</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="h-8 w-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
        </div>
      ) : !data || bosses.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <Shield className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No boss encounter data found for {team} team.</p>
          <p className="text-xs mt-1">Import combat logs to track boss kill times.</p>
        </div>
      ) : (
        <>
          {/* Best Kill Times Summary */}
          <div className="mb-8" data-testid="best-times-summary">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="h-4 w-4 text-yellow-400" />
              <span className="text-sm font-mono uppercase tracking-widest text-yellow-400">Best Kill Times</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {bosses.filter(b => b.best_kill_time).map((b) => (
                <button key={b.boss_name}
                  data-testid={`best-time-${b.boss_name.replace(/\s/g, "-")}`}
                  onClick={() => b.best_raid_id && navigate(`/raid/${b.best_raid_id}`)}
                  className="flex items-center gap-3 p-3 rounded-sm border border-yellow-500/10 bg-yellow-950/5 hover:bg-yellow-950/15 transition-colors text-left group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.boss_name}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">
                      {b.best_date} — {b.best_raid_name}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold font-mono text-yellow-400">{formatTime(b.best_kill_time)}</p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Per-Boss History */}
          <div className="space-y-6" data-testid="boss-history-list">
            {bosses.map((boss) => (
              <div key={boss.boss_name} className="border border-border/20 rounded-sm overflow-hidden" data-testid={`boss-${boss.boss_name.replace(/\s/g, "-")}`}>
                <div className="px-4 py-2.5 bg-card/30 border-b border-border/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-foreground/60" />
                    <span className="text-sm font-medium">{boss.boss_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {boss.best_kill_time && (
                      <Badge variant="outline" className="text-[10px] text-yellow-400 border-yellow-400/30 gap-1">
                        <Trophy className="h-2.5 w-2.5" /> {formatTime(boss.best_kill_time)}
                      </Badge>
                    )}
                    <span className="text-[10px] font-mono text-muted-foreground">
                      {boss.history.length} raid{boss.history.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {/* Timeline */}
                <div className="divide-y divide-border/10">
                  {[...boss.history].reverse().map((h, hi) => {
                    const isBest = h.kill_time === boss.best_kill_time && h.killed;
                    const prevEntry = boss.history.length > 1 && hi < boss.history.length - 1
                      ? [...boss.history].reverse()[hi + 1] : null;
                    const diff = prevEntry?.kill_time && h.kill_time
                      ? h.kill_time - prevEntry.kill_time : null;

                    return (
                      <button key={hi}
                        onClick={() => navigate(`/raid/${h.raid_id}`)}
                        className={`w-full px-4 py-2 flex items-center gap-3 hover:bg-card/30 transition-colors text-left ${isBest ? "bg-yellow-950/10" : ""}`}
                        data-testid={`history-entry-${boss.boss_name.replace(/\s/g, "-")}-${hi}`}
                      >
                        <span className="text-xs font-mono text-muted-foreground w-24 flex-shrink-0">{h.date}</span>
                        <span className="text-xs text-muted-foreground truncate flex-1">{h.raid_name}</span>

                        {h.wipe_count > 0 && (
                          <Badge variant="outline" className="text-[9px] text-destructive border-destructive/30 gap-0.5">
                            <Skull className="h-2.5 w-2.5" /> {h.wipe_count}w
                          </Badge>
                        )}

                        {h.killed ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-sm font-bold font-mono ${isBest ? "text-yellow-400" : "text-foreground"}`}>
                              {formatTime(h.kill_time)}
                            </span>
                            {diff !== null && diff !== 0 && (
                              <span className={`text-[10px] font-mono flex items-center gap-0.5 ${diff < 0 ? "text-green-400" : "text-red-400"}`}>
                                {diff < 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                                {diff > 0 ? "+" : ""}{formatTime(Math.abs(diff))}
                              </span>
                            )}
                            {isBest && <Trophy className="h-3.5 w-3.5 text-yellow-400 flex-shrink-0" />}
                          </div>
                        ) : (
                          <span className="text-xs text-destructive font-mono flex-shrink-0">No kill</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
